import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import BlockIcon from '@mui/icons-material/Block'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import { Deposit, DepositStatus, Redemption, RedemptionStatus } from '../api/types'
import { useOpsApi } from '../api/context'
import { apiError } from '../api/errors'
import { useCurrentUser } from '../auth'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { InfoHint } from '../components/InfoHint'
import { PageHeader } from '../components/PageHeader'
import { QueueColumn, QueueTable } from '../components/QueueTable'
import { SegmentedTabs } from '../components/SegmentedTabs'
import { StatusChip } from '../components/StatusChip'
import { tokens } from '../theme'
import { CANCELLABLE_REDEMPTION, RedemptionDetailDialog } from './odkupy/RedemptionDetailDialog'
import { formatDate, formatUnits4 } from './odkupy/format'
import { ConfirmPaymentDialog } from './platby/ConfirmPaymentDialog'
import { ManualDepositDialog } from './platby/ManualDepositDialog'
import { RedemptionRequestDialog } from './platby/RedemptionRequestDialog'
import { PaymentInfoDialog } from './platby/PaymentInfoDialog'
import { formatCzk, formatDateTime } from './platby/format'

/**
 * Záložky fáze. Vklady i výběry jdou stejnou cestou: čeká na potvrzení (platba /
 * podepsaná žádost) → zpracování (přidělení jednotek / fronta na Den ocenění a
 * potvrzení CEO) → hotovo.
 */
type PaymentTab = 'PENDING' | 'PROCESSING' | 'DONE'

const DEPOSIT_TAB: Partial<Record<DepositStatus, PaymentTab>> = {
  WAITING_PAYMENT: 'PENDING',
  PAID: 'PROCESSING',
  IN_BATCH: 'PROCESSING',
  POSTED: 'DONE',
}

const REDEMPTION_TAB: Record<RedemptionStatus, PaymentTab> = {
  NEW: 'PENDING',
  QUEUED: 'PROCESSING',
  REQUESTED: 'PROCESSING',
  CONFIRMED: 'DONE',
  PAID_OUT: 'DONE',
  CANCELLED: 'DONE',
}

/** Řádek společné tabulky: vklad, nebo výběr (odkup). */
type MoneyRow =
  | { kind: 'DEPOSIT'; id: string; tab: PaymentTab; deposit: Deposit }
  | { kind: 'REDEMPTION'; id: string; tab: PaymentTab; redemption: Redemption }

function clientName(row: MoneyRow): string {
  return row.kind === 'DEPOSIT' ? row.deposit.clientName : row.redemption.clientName
}

function strategyOf(row: MoneyRow): string {
  return row.kind === 'DEPOSIT' ? row.deposit.strategy : row.redemption.fundKey
}

function createdAtOf(row: MoneyRow): string {
  return row.kind === 'DEPOSIT' ? row.deposit.createdAt : row.redemption.createdAt
}

/** Částka pro řazení: vklad přijatá/sjednaná, výběr čistá výplata (nebo zadaná částka). */
function amountOf(row: MoneyRow): number {
  if (row.kind === 'DEPOSIT') return row.deposit.amountReceived ?? row.deposit.amount
  const r = row.redemption
  return r.unitPrice ? r.netAmount : r.requestedAmount ?? 0
}

/** Pilulka ve stylu StatusChip (neutrální odznaky vedle stavu a typ řádku). */
function Badge({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '9999px',
        fontWeight: 600,
        fontSize: '0.75rem',
        padding: '2px 10px',
        whiteSpace: 'nowrap',
        bgcolor: bg,
        color,
      }}
    >
      {label}
    </Box>
  )
}

/** Neutrální odznak „ruční" vedle stavu ručně zadaného vkladu (styl jako StatusChip). */
function ManualBadge() {
  return <Badge label="ruční" bg={tokens.neutral[100]} color={tokens.neutral[600]} />
}

function TypeBadge({ kind }: { kind: MoneyRow['kind'] }) {
  return kind === 'DEPOSIT' ? (
    <Badge label="Vklad" bg={tokens.success[100]} color={tokens.success[700]} />
  ) : (
    <Badge label="Výběr" bg={tokens.primary[100]} color={tokens.primary[800]} />
  )
}

function Caption({ children, error }: { children: ReactNode; error?: boolean }) {
  return (
    <Typography
      variant="caption"
      color={error ? 'error.main' : 'text.secondary'}
      component="div"
    >
      {children}
    </Typography>
  )
}

/** Co výběr zadal klient, dokud není oceněný (celá držba / jednotky / částka). */
function requestedText(r: Redemption): string {
  if (r.redeemAll) return 'celá držba'
  if (r.requestedUnits) return `${formatUnits4(r.requestedUnits)} j.`
  if (r.requestedAmount) return formatCzk(r.requestedAmount)
  return '—'
}

/**
 * Fáze 3 — Vklady / výběry. Vklady čekají na platbu (WAITING_PAYMENT), výběry na
 * podepsanou žádost (NEW). Po potvrzení jde vklad do Přidělení jednotek a výběr do
 * fronty na Den ocenění; oceněný výběr potvrzuje CEO v Kontrole přidělení a tady
 * se pak označí „Vyplaceno". Backoffice potvrzuje platby a žádosti, maže
 * nezaplacené vklady, stornuje nepotvrzené výběry a zadává ruční vklady (F3-A);
 * ostatní vidí read-only, QR platbu vidí všichni (F2-C).
 */
export function PlatbyPhase() {
  const api = useOpsApi()
  const user = useCurrentUser()
  const isBackoffice = user.isBackoffice
  const isCeo = user.isCeo

  const [deposits, setDeposits] = useState<Deposit[]>([])
  const [redemptions, setRedemptions] = useState<Redemption[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [tab, setTab] = useState<PaymentTab>('PENDING')

  const [confirmTarget, setConfirmTarget] = useState<Deposit | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Deposit | null>(null)
  const [paymentTarget, setPaymentTarget] = useState<Deposit | null>(null)
  const [manualOpen, setManualOpen] = useState(false)
  const [redemptionOpen, setRedemptionOpen] = useState(false)
  // Mazání běží — blokuje druhý klik na „smazat" (dialog se zavírá až po odpovědi).
  const [deleting, setDeleting] = useState(false)
  const [snackbar, setSnackbar] = useState<string | null>(null)

  // Výběry: detail + akce (potvrzení žádosti, výplata, storno).
  const [detail, setDetail] = useState<Redemption | null>(null)
  const [actionTarget, setActionTarget] = useState<Redemption | null>(null)
  const [action, setAction] = useState<'ACCEPT' | 'PAYOUT' | 'CANCEL' | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [list, red] = await Promise.all([api.listDeposits(), api.listRedemptions()])
      setDeposits(list)
      setRedemptions(red)
      setLoadError(null)
    } catch (err) {
      setLoadError(apiError(err, 'Načtení fronty selhalo'))
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => {
    reload()
  }, [reload])

  const rows = useMemo(() => {
    const all: MoneyRow[] = []
    deposits.forEach((d) => {
      const t = DEPOSIT_TAB[d.status]
      if (t) all.push({ kind: 'DEPOSIT', id: 'D' + d.id, tab: t, deposit: d })
    })
    redemptions.forEach((r) => {
      const t = REDEMPTION_TAB[r.status]
      if (t) all.push({ kind: 'REDEMPTION', id: 'R' + r.id, tab: t, redemption: r })
    })
    return all
  }, [deposits, redemptions])

  const byTab = useMemo(() => {
    const map: Record<PaymentTab, MoneyRow[]> = { PENDING: [], PROCESSING: [], DONE: [] }
    rows.forEach((row) => map[row.tab].push(row))
    return map
  }, [rows])

  const handleConfirmed = () => {
    setConfirmTarget(null)
    setSnackbar('Platba potvrzena — vklad se přesunul do fronty přidělení')
    reload()
  }

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return
    setDeleting(true)
    try {
      await api.deleteDeposit(deleteTarget.id)
      setSnackbar('Vklad smazán')
    } catch (err) {
      setSnackbar(apiError(err, 'Smazání selhalo'))
    } finally {
      setDeleting(false)
      setDeleteTarget(null)
      reload()
    }
  }

  const handleManualCreated = () => {
    setManualOpen(false)
    setTab('PROCESSING')
    setSnackbar('Ruční vklad vytvořen')
    reload()
  }

  const openDetail = async (redemption: Redemption) => {
    setDetail(redemption)
    // Kontroly a tranše raději čteme čerstvé.
    try {
      setDetail(await api.getRedemption(redemption.id))
    } catch {
      // Zůstane detail z fronty.
    }
  }

  const startAction = (redemption: Redemption, kind: 'ACCEPT' | 'PAYOUT' | 'CANCEL') => {
    setActionError(null)
    setActionTarget(redemption)
    setAction(kind)
  }

  const closeAction = () => {
    if (submitting) return
    setAction(null)
    setActionTarget(null)
  }

  const runAction = async () => {
    if (!actionTarget || !action) return
    const id = actionTarget.id
    setSubmitting(true)
    setActionError(null)
    try {
      let updated: Redemption
      let message: string
      if (action === 'ACCEPT') {
        updated = await api.acceptRedemption(id)
        message = updated.valuationDay
          ? `Žádost potvrzena. Výběr je ve frontě na Den ocenění ${formatDate(updated.valuationDay)}.`
          : 'Žádost potvrzena. Výběr je ve frontě na Den ocenění.'
      } else if (action === 'PAYOUT') {
        updated = await api.payoutRedemption(id)
        message = 'Výběr označen jako vyplacený.'
      } else {
        updated = await api.cancelRedemption(id)
        message = 'Žádost o výběr stornována.'
      }
      setSnackbar(message)
      setAction(null)
      setActionTarget(null)
      setTab(REDEMPTION_TAB[updated.status])
      if (detail && detail.id === updated.id) setDetail(updated)
      await reload()
    } catch (err) {
      setActionError(apiError(err, 'Akce s výběrem selhala'))
    } finally {
      setSubmitting(false)
    }
  }

  const depositActions = (d: Deposit) =>
    d.status !== 'WAITING_PAYMENT' ? null : (
      <>
        <Tooltip title="QR platba">
          <IconButton size="small" onClick={() => setPaymentTarget(d)}>
            <QrCode2Icon fontSize="small" />
          </IconButton>
        </Tooltip>
        {isBackoffice && (
          <Button size="small" variant="contained" onClick={() => setConfirmTarget(d)}>
            Zaplaceno
          </Button>
        )}
        {isBackoffice && (
          <Tooltip title="Smazat">
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(d)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </>
    )

  const redemptionActions = (r: Redemption) => (
    <>
      <Button size="small" variant="text" onClick={() => openDetail(r)}>
        Detail
      </Button>
      {r.status === 'NEW' && isBackoffice && (
        <Button size="small" variant="contained" onClick={() => startAction(r, 'ACCEPT')}>
          Potvrdit
        </Button>
      )}
      {r.status === 'CONFIRMED' && isCeo && (
        <Button size="small" variant="contained" onClick={() => startAction(r, 'PAYOUT')}>
          Vyplaceno
        </Button>
      )}
      {CANCELLABLE_REDEMPTION.includes(r.status) && isBackoffice && (
        <Tooltip title="Stornovat">
          <IconButton size="small" color="error" onClick={() => startAction(r, 'CANCEL')}>
            <BlockIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  )

  const renderAmount = (row: MoneyRow) => {
    if (row.kind === 'DEPOSIT') {
      const d = row.deposit
      const received = d.amountReceived ?? d.amount
      return (
        <Box>
          <Typography variant="body2">{formatCzk(received)}</Typography>
          {d.amountReceived != null && d.amountReceived !== d.amount && (
            <Caption>sjednáno {formatCzk(d.amount)}</Caption>
          )}
        </Box>
      )
    }
    const r = row.redemption
    if (!r.unitPrice) {
      return (
        <Box>
          <Typography variant="body2">{requestedText(r)}</Typography>
          <Caption>ocení se k Dni ocenění</Caption>
        </Box>
      )
    }
    return (
      <Box>
        <Typography variant="body2">
          <strong>{formatCzk(r.netAmount)}</strong>
        </Typography>
        <Caption>hrubá {formatCzk(r.grossAmount)}</Caption>
      </Box>
    )
  }

  const renderStatus = (row: MoneyRow) => {
    if (row.kind === 'DEPOSIT') {
      const d = row.deposit
      return (
        <Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <StatusChip status={d.status} />
            {d.manual && <ManualBadge />}
            {d.note && (
              <Tooltip title={d.note}>
                <InfoOutlinedIcon fontSize="small" sx={{ color: tokens.neutral[400] }} />
              </Tooltip>
            )}
          </Box>
          {d.paidAt && (
            <Caption>
              {formatDateTime(d.paidAt)}
              {d.paidBy ? ` · ${d.paidBy}` : ''}
            </Caption>
          )}
        </Box>
      )
    }
    const r = row.redemption
    let caption: ReactNode = null
    if (r.status === 'NEW') caption = 'čeká na podepsanou žádost'
    else if (r.status === 'QUEUED')
      caption = r.valuationDay ? `Den ocenění ${formatDate(r.valuationDay)}` : 'čeká na Den ocenění'
    else if (r.status === 'REQUESTED') caption = 'potvrzuje CEO v Kontrole přidělení'
    else if (r.status === 'CONFIRMED')
      caption = r.payoutDue ? `k výplatě do ${formatDate(r.payoutDue)}` : 'k výplatě'
    else if (r.status === 'PAID_OUT' && r.paidAt) caption = formatDateTime(r.paidAt)
    return (
      <Box>
        <StatusChip status={r.status} />
        {caption && <Caption>{caption}</Caption>}
        {r.queueError && r.status === 'QUEUED' && <Caption error>{r.queueError}</Caption>}
      </Box>
    )
  }

  const columns: QueueColumn<MoneyRow>[] = [
    { key: 'kind', label: 'Typ', getSortValue: (row) => row.kind, render: (row) => <TypeBadge kind={row.kind} /> },
    {
      key: 'client',
      label: 'Klient',
      getSortValue: clientName,
      render: clientName,
    },
    {
      key: 'amount',
      label: 'Částka',
      align: 'right',
      getSortValue: amountOf,
      render: renderAmount,
    },
    { key: 'strategy', label: 'Strategie', getSortValue: strategyOf, render: strategyOf },
    {
      key: 'createdAt',
      label: 'Vytvořeno',
      getSortValue: createdAtOf,
      render: (row) => (
        <Box>
          <Typography variant="body2">{formatDateTime(createdAtOf(row))}</Typography>
          {row.kind === 'REDEMPTION' && row.redemption.receivedDate && (
            <Caption>doručeno {formatDate(row.redemption.receivedDate)}</Caption>
          )}
        </Box>
      ),
    },
    { key: 'status', label: 'Stav', render: renderStatus },
    {
      key: 'actions',
      label: 'Akce',
      align: 'right',
      render: (row) => (
        <Box
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}
        >
          {row.kind === 'DEPOSIT' ? depositActions(row.deposit) : redemptionActions(row.redemption)}
        </Box>
      ),
    },
  ]

  const toolbar: ReactNode = (
    <>
      <SegmentedTabs
        items={[
          { value: 'PENDING', label: `Čeká na potvrzení (${byTab.PENDING.length})` },
          { value: 'PROCESSING', label: `Zpracování (${byTab.PROCESSING.length})` },
          { value: 'DONE', label: `Hotovo (${byTab.DONE.length})` },
        ]}
        value={tab}
        onChange={(value) => setTab(value as PaymentTab)}
      />
      {isBackoffice && (
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setManualOpen(true)}>
          Ruční vklad
        </Button>
      )}
      {isBackoffice && (
        <Button variant="outlined" onClick={() => setRedemptionOpen(true)}>
          Žádost o výběr
        </Button>
      )}
    </>
  )

  const emptyText: Record<PaymentTab, string> = {
    PENDING: 'Nic nečeká na potvrzení',
    PROCESSING: 'Nic se nezpracovává',
    DONE: 'Zatím nic hotového',
  }

  const showInitialLoader = loading && deposits.length === 0 && redemptions.length === 0

  const actionDialog = (() => {
    const r = actionTarget
    if (action === 'ACCEPT') {
      return {
        title: 'Potvrdit žádost o výběr',
        text: r
          ? `Podepsaná žádost klienta ${r.clientName} došla. Výběr půjde do fronty na Den ocenění. Ocení se při ocenění fondu ${r.fundKey}.`
          : '',
        confirmLabel: 'Potvrdit',
        danger: false,
      }
    }
    if (action === 'PAYOUT') {
      return {
        title: 'Označit výběr jako vyplacený',
        text: r ? `Z banky odešlo ${formatCzk(r.netAmount)} klientovi ${r.clientName}.` : '',
        confirmLabel: 'Označit jako vyplacené',
        danger: false,
      }
    }
    return {
      title: 'Stornovat žádost o výběr',
      text: r ? `Žádost klienta ${r.clientName} se zruší. V core se nic nemění.` : '',
      confirmLabel: 'stornovat',
      danger: true,
    }
  })()

  return (
    <Box sx={{ maxWidth: 1100, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Vklady / výběry"
        subtitle="Vklad čeká na platbu, výběr na podepsanou žádost. Po potvrzení jde vklad do Přidělení jednotek a výběr do fronty na Den ocenění."
        info={
          <InfoHint
            title="Jak vklady a výběry probíhají"
            lines={[
              'Skutečně přijatá částka je předvyplněná sjednanou — lze upravit; jednotky se pak počítají z PŘIJATÉ částky.',
              'Potvrzením vznikne klientovi vklad hotovosti na peněžním trhu (core deal BUY, CZK účet portfolia).',
              'Volitelně odejde klientovi potvrzovací email o přijetí platby.',
              'Vklad se přesune do fronty Přidělení jednotek (stav ZAPLACENO).',
              'Ruční vklad („peníze už přišly") vzniká rovnou jako ZAPLACENO a frontu plateb přeskočí.',
              'Výběr: žádost se založí jako Nová. Až dojde podepsaná žádost, potvrďte ji.',
              'Potvrzený výběr čeká ve frontě na Den ocenění (nejdřív 14 dní po doručení).',
              'Ocení se v Přidělení jednotek. Potvrzuje ho CEO v Kontrole přidělení.',
              'Po odeslání peněz z banky CEO označí výběr jako Vyplaceno.',
              'Stornovat lze jen výběr, který CEO ještě nepotvrdil.',
            ]}
          />
        }
      />

      {loadError && <Alert severity="error">{loadError}</Alert>}

      {showInitialLoader ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <QueueTable
          // key: bez remountu by React zachoval stav řazení/hledání z jiného tabu
          // (stejný typ komponenty na stejné pozici) a defaultSortKey by se neuplatnil.
          key={tab}
          columns={columns}
          rows={byTab[tab]}
          getRowKey={(row) => row.id}
          getSearchText={(row) => `${clientName(row)} ${strategyOf(row)}`}
          searchPlaceholder="Hledat klienta nebo strategii…"
          defaultSortKey="createdAt"
          defaultSortDir="desc"
          emptyText={emptyText[tab]}
          toolbar={toolbar}
        />
      )}

      <ConfirmPaymentDialog
        open={Boolean(confirmTarget)}
        deposit={confirmTarget}
        onClose={() => setConfirmTarget(null)}
        onConfirmed={handleConfirmed}
      />

      <PaymentInfoDialog
        open={Boolean(paymentTarget)}
        deposit={paymentTarget}
        onClose={() => setPaymentTarget(null)}
      />

      <RedemptionRequestDialog
        open={redemptionOpen}
        onClose={() => setRedemptionOpen(false)}
        onCreated={() => {
          setRedemptionOpen(false)
          setTab('PENDING')
          setSnackbar('Žádost o výběr založena. Až dojde podepsaná žádost, potvrďte ji.')
          reload()
        }}
      />
      <ManualDepositDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onCreated={handleManualCreated}
      />

      <RedemptionDetailDialog
        open={Boolean(detail)}
        redemption={detail}
        submitting={submitting}
        isCeo={isCeo}
        isBackoffice={isBackoffice}
        onAcceptClick={() => detail && startAction(detail, 'ACCEPT')}
        onPayoutClick={() => detail && startAction(detail, 'PAYOUT')}
        onCancelClick={() => detail && startAction(detail, 'CANCEL')}
        onClose={() => setDetail(null)}
      />

      <ConfirmDialog
        open={Boolean(action && actionTarget)}
        title={actionDialog.title}
        text={actionDialog.text}
        confirmLabel={actionDialog.confirmLabel}
        danger={actionDialog.danger}
        doubleConfirm={actionDialog.danger}
        submitting={submitting}
        error={actionError}
        onConfirm={runAction}
        onClose={closeAction}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Smazat vklad"
        text={
          deleteTarget
            ? `Opravdu smazat vklad klienta ${deleteTarget.clientName} na ${formatCzk(deleteTarget.amount)}?`
            : ''
        }
        confirmLabel="smazat"
        danger
        doubleConfirm
        submitting={deleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snackbar ?? ''}
      />
    </Box>
  )
}
